import type {
  DroppedAction,
  ReimportCategory,
  ReimportChainField,
  ReimportChainRow,
  ReimportComparison,
  ReimportDecisions,
  ReimportField,
  ReimportFieldDiff,
  ReimportStepView,
  ReimportTestCaseRow,
} from '@testflow/shared';
import { DROPPED_ACTIONS } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel, StateChip } from '../../components/atoms.js';

// Bundle C (#125) — die Abgleich-Vorschau. EIN Bedienmuster für Testfälle und Ketten: Häkchen =
// übernehmen ja/nein, je Konfliktfeld der Umschalter „TestFlow behalten" (Vorgabe, Q6) / „Datei
// übernehmen", je entfallendem Testfall die Aktion (Vorgabe „Reifegrad: nicht im Scope", Q4).
// Grundsatz: nichts geht still verloren — Kollisionen, zurückgehaltene Ketten und fehlende Glieder
// stehen hier, BEVOR der Admin committet. Der Server rechnet dieselben Regeln beim Commit noch
// einmal (resolveDecisions/settleNewChains); die Oberfläche schickt den vollständigen Stand mit,
// damit das Audit-Log ihn zeigt.

export interface TcChoice {
  selected: boolean;
  take_file: ReimportField[];
  dropped_action: DroppedAction;
}
export interface ChainChoice {
  selected: boolean;
  take_file: ReimportChainField[];
}
export interface ReimportChoices {
  tcs: Record<string, TcChoice>;
  chains: Record<string, ChainChoice>;
}

const isCollision = (r: { collision: unknown[] }) => r.collision.length > 0;

// Vorgabe wie serverseitig: ausgewählt — außer einer „neu"-Zeile, die schon in einer Basis stand
// (früher abgewählt oder gelöscht). Kollisionen sind nie gewählt.
function defaultTc(r: ReimportTestCaseRow): TcChoice {
  return {
    selected: !isCollision(r) && !(r.category === 'neu' && r.deleted_in_testflow),
    take_file: [],
    dropped_action: 'nicht_im_scope',
  };
}
const defaultChain = (r: ReimportChainRow): ChainChoice => ({
  selected: !isCollision(r),
  take_file: [],
});

const tcChoice = (ch: ReimportChoices, r: ReimportTestCaseRow): TcChoice =>
  ch.tcs[r.key] ?? defaultTc(r);
const chainChoice = (ch: ReimportChoices, r: ReimportChainRow): ChainChoice =>
  ch.chains[r.key] ?? defaultChain(r);

export function initialChoices(c: ReimportComparison): ReimportChoices {
  const tcs: Record<string, TcChoice> = {};
  for (const r of c.test_cases) tcs[r.key] = defaultTc(r);
  const chains: Record<string, ChainChoice> = {};
  for (const r of c.chains) chains[r.key] = defaultChain(r);
  return { tcs, chains };
}

// Nach „Vorschau neu laden" (409 reimport_preview_stale) gilt ein neuer Fingerabdruck — die
// Entscheidungen des Admins sollen dabei nicht still auf die Vorgaben zurückfallen. Übernommen wird
// je Schlüssel, solange die Zeile noch existiert und dieselbe Kategorie hat (eine andere Kategorie
// heißt: anderer Sachverhalt, die alte Entscheidung passt nicht mehr). „Datei übernehmen" nur für
// Felder, deren Konflikt in alter und neuer Vorschau GLEICH aussieht (TestFlow-Wert, Dateiwert,
// Grund, ggf. Schritte) — sonst würde ein Dateiwert committet, den der Admin nie gewählt gesehen
// hat (Q6: Vorgabe ist „TestFlow behalten"; der Fingerabdruck verspricht „entschieden über genau
// diesen Stand"). „löschen" nur, wenn der Server es noch erlaubt. Alles andere bekommt die
// Vorgabe — der Hinweis in der Vorschau bittet um Prüfung.
export function carryOverChoices(
  next: ReimportComparison,
  prev: ReimportChoices,
  before: ReimportComparison,
): ReimportChoices {
  const out = initialChoices(next);
  const sig = <F extends string>(d: ReimportFieldDiff<F>) =>
    JSON.stringify([d.tf, d.file, d.conflict_reason, d.steps ?? null]);
  // Felder, deren Umschalter weitergelten darf: in der neuen Vorschau ein offener Konflikt (keine
  // Kollision) und in der alten derselbe Konflikt mit denselben Werten.
  const unchangedConflicts = <F extends string>(
    now: ReimportFieldDiff<F>[],
    was: ReimportFieldDiff<F>[],
  ) => {
    const old = new Map(was.filter((d) => d.outcome === 'conflict').map((d) => [d.field, sig(d)]));
    return new Set(
      now
        .filter(
          (d) =>
            d.outcome === 'conflict' &&
            d.conflict_reason !== 'collision' &&
            old.get(d.field) === sig(d),
        )
        .map((d) => d.field),
    );
  };
  const oldTcs = new Map(before.test_cases.map((r) => [r.key, r]));
  for (const r of next.test_cases) {
    const old = oldTcs.get(r.key);
    const x = prev.tcs[r.key];
    if (!old || !x || old.category !== r.category || isCollision(r) || isCollision(old)) continue;
    const open = unchangedConflicts(r.diffs, old.diffs);
    out.tcs[r.key] = {
      selected: x.selected,
      take_file: x.take_file.filter((f) => open.has(f)),
      dropped_action:
        x.dropped_action === 'loeschen' && !r.deletable ? 'nicht_im_scope' : x.dropped_action,
    };
  }
  const oldChains = new Map(before.chains.map((r) => [r.key, r]));
  for (const r of next.chains) {
    const old = oldChains.get(r.key);
    const x = prev.chains[r.key];
    if (!old || !x || old.category !== r.category || isCollision(r) || isCollision(old)) continue;
    const open = unchangedConflicts(r.diffs, old.diffs);
    out.chains[r.key] = {
      selected: x.selected,
      take_file: x.take_file.filter((f) => open.has(f)),
    };
  }
  return out;
}

// Je Zeile genau ein Eintrag (der Server lehnt doppelte Schlüssel ab). Kollisionszeilen: nur
// `selected: false` — der Server ignoriert sie, das Audit zeigt den Stand. entfällt-Zeilen tragen
// die Aktion, alle anderen ihre Umschalter. entfällt-Ketten haben keine Aktion.
export function toDecisions(c: ReimportComparison, ch: ReimportChoices): ReimportDecisions {
  return {
    comparison_hash: c.hash,
    test_cases: c.test_cases.map((r) => {
      if (isCollision(r)) return { key: r.key, selected: false };
      const x = tcChoice(ch, r);
      return r.category === 'entfaellt'
        ? { key: r.key, selected: true, dropped_action: x.dropped_action }
        : { key: r.key, selected: x.selected, take_file: x.take_file };
    }),
    chains: c.chains.map((r) => {
      if (isCollision(r)) return { key: r.key, selected: false };
      const x = chainChoice(ch, r);
      return { key: r.key, selected: x.selected, take_file: x.take_file };
    }),
  };
}

// Ein Feld wird geschrieben: kein Konflikt, oder Konflikt (außer Kollision) auf „Datei" umgestellt.
function applies<F extends string>(d: ReimportFieldDiff<F>, takeFile: readonly F[]): boolean {
  if (d.outcome === 'take_file') return true;
  return d.conflict_reason !== 'collision' && takeFile.includes(d.field);
}

export interface ReimportOutlook {
  createdTcs: Set<string>; // neue Testfälle, die wirklich angelegt werden
  heldBack: Map<string, string>; // Testfall-Schlüssel → Kette, mit der er zurückgehalten wird
  chainsSkipped: Map<string, string[]>; // neue Kette → Glieder, die ihr fehlen (nicht angelegt)
  missingMembers: Map<string, string[]>; // bestehende Kette → Glieder ohne Testfall nach dem Commit
}

// Spiegel von settleNewChains (apps/api/src/import/reimport-apply.ts, #125), damit die Vorschau
// VOR dem Commit sagt, was CommitResult.chains_skipped_incomplete / test_cases_held_back /
// unresolved_members sonst erst hinterher meldet:
//  1. Eine neue Kette entsteht nur, wenn jedes Glied danach einen Katalog-Testfall hat.
//  2. Entsteht eine neue Kette nicht, werden ihre über die Kette geschlüsselten neuen Glieder
//     (`kette:<kette>#…`) zurückgehalten. Beides bis zum Fixpunkt.
export function outlookOf(c: ReimportComparison, ch: ReimportChoices): ReimportOutlook {
  const createdTcs = new Set(
    c.test_cases
      .filter((r) => r.category === 'neu' && !isCollision(r) && tcChoice(ch, r).selected)
      .map((r) => r.key),
  );
  const matchedTcs = new Set(
    c.test_cases
      .filter((r) => r.test_case_id !== null && r.category !== 'entfaellt' && !isCollision(r))
      .map((r) => r.key),
  );
  const created = new Set(
    c.chains
      .filter((r) => r.category === 'neu' && !isCollision(r) && chainChoice(ch, r).selected)
      .map((r) => r.key),
  );
  const heldBack = new Map<string, string>();
  const chainsSkipped = new Map<string, string[]>();
  const written = () => new Set([...matchedTcs, ...createdTcs]);
  for (let changed = true; changed; ) {
    changed = false;
    const w = written();
    for (const r of c.chains) {
      if (!created.has(r.key)) continue;
      const missing = r.member_keys.filter((k) => !w.has(k));
      if (missing.length > 0) {
        created.delete(r.key);
        chainsSkipped.set(r.key, missing);
        changed = true;
      }
    }
    for (const r of c.chains) {
      if (created.has(r.key) || r.e2e_id !== null || r.category === 'entfaellt') continue;
      if (isCollision(r)) continue;
      const prefix = `kette:${r.key}#`;
      for (const k of r.member_keys) {
        if (k.startsWith(prefix) && createdTcs.has(k)) {
          createdTcs.delete(k);
          heldBack.set(k, r.key);
          changed = true;
        }
      }
    }
  }
  const w = written();
  const missingMembers = new Map<string, string[]>();
  for (const r of c.chains) {
    if (r.e2e_id === null || isCollision(r)) continue;
    const x = chainChoice(ch, r);
    const membersWritten = r.diffs.some((d) => d.field === 'members' && applies(d, x.take_file));
    if (!x.selected || !membersWritten) continue;
    const missing = r.member_keys.filter((k) => !w.has(k));
    if (missing.length > 0) missingMembers.set(r.key, missing);
  }
  return { createdTcs, heldBack, chainsSkipped, missingMembers };
}

export interface ReimportCounts {
  neu: number;
  geaendert: number;
  entfaellt: number;
  chainsNew: number;
  chainsUpdated: number;
}

// Zähler für die Rückfrage — nur, was der Commit wirklich schreibt (abgewählt, zurückgehalten und
// Konflikte mit „TestFlow behalten" zählen nicht).
// Sicherheitsnetz (#125, Fix-Runde 2): ein falsch gemapptes Blatt liefert keine Testfälle in den
// Entwurf, alle seine Testfälle erscheinen dann als „entfällt". Ab 5 oder ab 10 % der verglichenen
// Testfälle weisen wir deshalb vor der Prüfung darauf hin. Gezählt wird die Kategorie der Zeilen
// (was die Datei hergibt), nicht die Wahl des Admins — die Warnung ist ein Blick auf die Datei.
// Eine Aufschlüsselung je Blatt gibt der Abgleich nicht her (die Zeilen kennen ihr Blatt nicht).
export function droppedWarningCount(c: ReimportComparison): number | null {
  const n = c.test_cases.filter((r) => r.category === 'entfaellt').length;
  if (n === 0) return null;
  return n >= 5 || n / c.test_cases.length >= 0.1 ? n : null;
}

export function reimportCounts(c: ReimportComparison, ch: ReimportChoices): ReimportCounts {
  const o = outlookOf(c, ch);
  const updatesTc = (r: ReimportTestCaseRow) => {
    const x = tcChoice(ch, r);
    return x.selected && r.diffs.some((d) => applies(d, x.take_file));
  };
  const updatesChain = (r: ReimportChainRow) => {
    const x = chainChoice(ch, r);
    return x.selected && r.diffs.some((d) => applies(d, x.take_file));
  };
  const chainsNew = c.chains.filter(
    (r) =>
      r.category === 'neu' &&
      !isCollision(r) &&
      chainChoice(ch, r).selected &&
      !o.chainsSkipped.has(r.key),
  ).length;
  return {
    neu: o.createdTcs.size,
    geaendert: c.test_cases.filter(
      (r) =>
        r.test_case_id !== null && r.category !== 'entfaellt' && !isCollision(r) && updatesTc(r),
    ).length,
    // Wie reimport-apply: „nicht im Scope" an einer Zeile, die schon so steht, schreibt nichts.
    entfaellt: c.test_cases.filter((r) => {
      if (r.category !== 'entfaellt') return false;
      const a = tcChoice(ch, r).dropped_action;
      return a === 'loeschen' || (a === 'nicht_im_scope' && r.readiness !== 'nicht_im_scope');
    }).length,
    chainsNew,
    chainsUpdated: c.chains.filter((r) => r.e2e_id !== null && !isCollision(r) && updatesChain(r))
      .length,
  };
}

const ORDER: ReimportCategory[] = ['konflikt', 'geaendert', 'neu', 'entfaellt', 'unveraendert'];
const TONE: Record<ReimportCategory, 'default' | 'ok' | 'warn' | 'fail'> = {
  konflikt: 'fail',
  geaendert: 'warn',
  neu: 'ok',
  entfaellt: 'warn',
  unveraendert: 'default',
};
type Filter = 'all' | ReimportCategory;

const STEP_COLS = ['expected', 'input', 'tcode', 'notes'] as const;

const sameStep = (a: ReimportStepView | undefined, b: ReimportStepView | undefined) =>
  !!a &&
  !!b &&
  a.description === b.description &&
  STEP_COLS.every((col) => (a[col] ?? '') === (b[col] ?? ''));

// `other` = die Gegenseite (TestFlow ↔ Datei): ein Schritt, der dort an derselben Position anders
// lautet oder fehlt, ist markiert — bei langen Listen findet man die Änderung sonst nicht.
function StepList({ steps, other }: { steps: ReimportStepView[]; other: ReimportStepView[] }) {
  const { t } = useTranslation();
  if (steps.length === 0) return <span className="text-text-muted">—</span>;
  return (
    <ol className="list-decimal pl-4">
      {steps.map((s, i) => {
        const changed = !sameStep(s, other[i]);
        const cls = changed ? 'mb-1 border-l-2 border-status-warn pl-1' : 'mb-1';
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: Schrittnummer IST die Identität (Position)
          <li key={i} className={cls}>
            {changed && (
              <span className="mr-1 text-[10px] font-medium uppercase text-status-warn">
                {t('import.reimport.stepChanged')}
              </span>
            )}
            <span className="whitespace-pre-wrap break-words">{s.description}</span>
            {STEP_COLS.filter((col) => s[col]).map((col) => (
              <div key={col} className="text-text-muted">
                <span>{t(`import.reimport.stepCol.${col}`)}</span>
                {': '}
                <span className="whitespace-pre-wrap break-words text-text-body">{s[col]}</span>
              </div>
            ))}
          </li>
        );
      })}
    </ol>
  );
}

function DiffTable<F extends string>({
  diffs,
  takeFile,
  onToggle,
}: {
  diffs: ReimportFieldDiff<F>[];
  takeFile: F[];
  onToggle: (field: F, take: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <table className="mt-2 w-full table-fixed text-[11px]">
      <thead>
        <tr className="text-left text-text-muted">
          <th className="w-28 font-normal">{t('import.reimport.col.field')}</th>
          <th className="font-normal">{t('import.reimport.col.testflow')}</th>
          <th className="font-normal">{t('import.reimport.col.file')}</th>
          <th className="w-44 font-normal" />
        </tr>
      </thead>
      <tbody>
        {diffs.map((d) => {
          const take = applies(d, takeFile);
          return (
            <tr key={d.field} className="border-t border-[var(--border)] align-top">
              <td className="py-1 text-text-muted">{t(`import.reimport.field.${d.field}`)}</td>
              {/* max-h wirkt an einer Tabellenzelle nicht — der Scrollbereich ist ein eigener Block. */}
              <td className="py-1 pr-2">
                <div className="max-h-48 overflow-y-auto">
                  {d.steps ? (
                    <StepList steps={d.steps.tf} other={d.steps.file} />
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{d.tf || '—'}</span>
                  )}
                </div>
              </td>
              <td className="py-1 pr-2">
                <div className="max-h-48 overflow-y-auto">
                  {d.steps ? (
                    <StepList steps={d.steps.file} other={d.steps.tf} />
                  ) : (
                    <span className="whitespace-pre-wrap break-words">{d.file || '—'}</span>
                  )}
                </div>
              </td>
              <td className="py-1">
                {d.outcome === 'conflict' ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-status-fail">
                      {t(`import.reimport.conflict.${d.conflict_reason ?? 'value'}`)}
                    </span>
                    {d.conflict_reason !== 'collision' && (
                      <div className="flex overflow-hidden rounded-chip border border-[var(--border)]">
                        <button
                          type="button"
                          aria-pressed={!take}
                          onClick={() => onToggle(d.field, false)}
                          className={`px-2 py-0.5 ${!take ? 'bg-brand-accent text-white' : 'text-text-muted'}`}
                        >
                          {t('import.reimport.keepTestflow')}
                        </button>
                        <button
                          type="button"
                          aria-pressed={take}
                          onClick={() => onToggle(d.field, true)}
                          className={`px-2 py-0.5 ${take ? 'bg-brand-accent text-white' : 'text-text-muted'}`}
                        >
                          {t('import.reimport.takeFile')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-text-muted">{t('import.reimport.willTake')}</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CollisionList({
  items,
}: {
  items: Array<{
    side: 'file' | 'catalog';
    human_id: string | null;
    source_ref: string | null;
    label: string;
    run_count?: number;
  }>;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 text-[11px]">
      <p className="text-status-fail">{t('import.reimport.collision.explain')}</p>
      <ul className="mt-1">
        {items.map((it, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Beteiligte haben keine eigene ID (Dateizeilen)
          <li key={i} className="flex items-center gap-2">
            <StateChip label={t(`import.reimport.collision.side.${it.side}`)} />
            {(it.human_id ?? it.source_ref) ? (
              <IdLabel
                id={it.human_id ?? it.source_ref ?? ''}
                secondary={it.human_id ? it.source_ref : null}
              />
            ) : null}
            <span className="text-text-body">{it.label}</span>
            {(it.run_count ?? 0) > 0 && (
              <StateChip
                label={t('import.reimport.runs', { count: it.run_count ?? 0 })}
                tone="warn"
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReimportReview({
  comparison,
  choices,
  onChange,
}: {
  comparison: ReimportComparison;
  choices: ReimportChoices;
  onChange: (c: ReimportChoices) => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  // Zugeklappt/aufgeklappt je Zeile; ohne Eintrag gilt: Konflikte offen (sie brauchen eine
  // Entscheidung), alles andere zu.
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const outlook = outlookOf(comparison, choices);

  const counts = Object.fromEntries(
    ORDER.map((c) => [
      c,
      comparison.test_cases.filter((r) => r.category === c).length +
        comparison.chains.filter((r) => r.category === c).length,
    ]),
  ) as Record<ReimportCategory, number>;
  const visible = (category: ReimportCategory) =>
    filter === 'all' ? category !== 'unveraendert' : category === filter;
  const byOrder = <R extends { category: ReimportCategory }>(a: R, b: R) =>
    ORDER.indexOf(a.category) - ORDER.indexOf(b.category);
  const tcRows = comparison.test_cases.filter((r) => visible(r.category)).sort(byOrder);
  const chainRows = comparison.chains.filter((r) => visible(r.category)).sort(byOrder);

  const tcByKey = new Map(comparison.test_cases.map((r) => [r.key, r]));
  const chainByKey = new Map(comparison.chains.map((r) => [r.key, r]));
  const tcLabel = (key: string) => tcByKey.get(key)?.title ?? key;
  const chainLabel = (key: string) => chainByKey.get(key)?.name ?? key;
  const titles = (keys: Iterable<string>) => [...keys].map(tcLabel).join(', ');

  const setTc = (r: ReimportTestCaseRow, patch: Partial<TcChoice>) =>
    onChange({
      ...choices,
      tcs: { ...choices.tcs, [r.key]: { ...tcChoice(choices, r), ...patch } },
    });
  const setChain = (r: ReimportChainRow, patch: Partial<ChainChoice>) =>
    onChange({
      ...choices,
      chains: { ...choices.chains, [r.key]: { ...chainChoice(choices, r), ...patch } },
    });
  const toggleField = <F extends string>(list: F[], field: F, take: boolean): F[] =>
    take ? [...new Set([...list, field])] : list.filter((f) => f !== field);

  const isOpen = (id: string, category: ReimportCategory) => open[id] ?? category === 'konflikt';
  // Zugeklappt nennt der Knopf die betroffenen Felder („Titel, Schritte") — gerade bei Zeilen mit
  // Läufen soll man ohne Aufklappen sehen, WAS sich ändert.
  const diffToggle = (id: string, category: ReimportCategory, fields: string[]) => (
    <button
      type="button"
      aria-expanded={isOpen(id, category)}
      onClick={() => setOpen((o) => ({ ...o, [id]: !isOpen(id, category) }))}
      className="text-[11px] text-brand-accent underline"
    >
      {isOpen(id, category)
        ? t('import.reimport.hideDiffs', { count: fields.length })
        : t('import.reimport.showDiffs', {
            fields: fields.map((f) => t(`import.reimport.field.${f}`)).join(', '),
          })}
    </button>
  );

  const missingTotal = [...outlook.missingMembers.values()].reduce((n, l) => n + l.length, 0);
  const hasOutlook =
    outlook.chainsSkipped.size > 0 || outlook.heldBack.size > 0 || missingTotal > 0;

  // Neue Ketten, die gerade WEGEN dieses (abgewählten) Testfalls nicht entstehen.
  const skippedBecause = (key: string) =>
    [...outlook.chainsSkipped]
      .filter(([, missing]) => missing.includes(key))
      .map(([ck]) => chainLabel(ck));

  return (
    <div className="mt-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <fieldset className="flex flex-wrap gap-3 text-xs">
        <legend className="sr-only">{t('import.reimport.filterLabel')}</legend>
        <button
          type="button"
          aria-pressed={filter === 'all'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'font-bold text-brand-accent' : 'text-text-muted'}
        >
          {t('import.reimport.filter.all')}
        </button>
        {ORDER.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={filter === c}
            onClick={() => setFilter(c)}
            className={filter === c ? 'font-bold text-brand-accent' : 'text-text-muted'}
          >
            {t(`import.reimport.filter.${c}`, { n: counts[c] })}
          </button>
        ))}
      </fieldset>

      {hasOutlook && (
        <output className="mt-3 block text-xs text-status-warn">
          {t('import.reimport.outlook', {
            // Je Teil eine eigene Pluralform; Teile mit 0 entfallen.
            parts: (
              [
                ['outlookChains', outlook.chainsSkipped.size],
                ['outlookHeldBack', outlook.heldBack.size],
                ['outlookMissing', missingTotal],
              ] as const
            )
              .filter(([, n]) => n > 0)
              .map(([k, n]) => t(`import.reimport.${k}`, { count: n }))
              .join(', '),
          })}
        </output>
      )}

      <p className="mt-4 text-[11px] uppercase tracking-wide text-text-muted">
        {t('import.reimport.testCasesTitle')}
      </p>
      {tcRows.length === 0 && (
        <p className="mt-1 text-xs text-text-muted">
          {t(
            chainRows.length > 0
              ? 'import.reimport.emptyFilterChainsOnly'
              : 'import.reimport.emptyFilter',
          )}
        </p>
      )}
      <ul className="mt-1">
        {tcRows.map((r) => {
          const ch = tcChoice(choices, r);
          const collision = isCollision(r);
          const selectable =
            !collision &&
            (r.category === 'neu' || r.category === 'geaendert' || r.category === 'konflikt');
          const id = r.human_id ?? r.source_ref;
          const heldBy = outlook.heldBack.get(r.key);
          const skipped = r.category === 'neu' && !ch.selected ? skippedBecause(r.key) : [];
          return (
            <li
              key={r.key}
              data-testid={`reimport-tc-${r.key}`}
              className="border-t border-[var(--border)] py-2 text-xs"
            >
              <div className="flex items-center gap-2">
                {selectable && (
                  <input
                    type="checkbox"
                    aria-label={t('import.reimport.select', { id: id ?? r.title })}
                    checked={ch.selected}
                    onChange={(e) => setTc(r, { selected: e.target.checked })}
                  />
                )}
                {id && <IdLabel id={id} secondary={r.human_id ? r.source_ref : null} />}
                <span className="min-w-0 flex-1 truncate text-text-body" title={r.title}>
                  {r.title}
                  {r.chain_ref && !r.source_ref && (
                    <span className="text-text-muted"> · {r.chain_ref}</span>
                  )}
                </span>
                <StateChip
                  label={t(`import.reimport.category.${r.category}`)}
                  tone={TONE[r.category]}
                />
                {r.run_count > 0 && (
                  <StateChip
                    label={t('import.reimport.runs', { count: r.run_count })}
                    tone="warn"
                  />
                )}
                {r.deleted_in_testflow && <StateChip label={t('import.reimport.notInCatalog')} />}
              </div>
              {heldBy && (
                <p className="mt-1 text-status-warn">
                  {t('import.reimport.heldBack', { chain: chainLabel(heldBy) })}
                </p>
              )}
              {skipped.length > 0 && (
                <p className="mt-1 text-status-warn">
                  {t('import.reimport.memberOf', { chain: skipped.join(', ') })}
                </p>
              )}
              {collision && (
                <CollisionList items={r.collision.map((it) => ({ ...it, label: it.title }))} />
              )}
              {r.diffs.length > 0 && (
                <div className="mt-1">
                  {diffToggle(
                    `tc:${r.key}`,
                    r.category,
                    r.diffs.map((d) => d.field),
                  )}
                  {isOpen(`tc:${r.key}`, r.category) && (
                    <DiffTable
                      diffs={r.diffs}
                      takeFile={ch.take_file}
                      onToggle={(field, take) =>
                        setTc(r, { take_file: toggleField(ch.take_file, field, take) })
                      }
                    />
                  )}
                </div>
              )}
              {r.category === 'entfaellt' && (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-text-muted">
                  <span>{t('import.reimport.droppedLabel')}</span>
                  <select
                    aria-label={t('import.reimport.droppedAction', { id: id ?? r.title })}
                    value={ch.dropped_action}
                    onChange={(e) => setTc(r, { dropped_action: e.target.value as DroppedAction })}
                    className="rounded-btn border border-[var(--border)] bg-bg-subtle px-1 py-0.5 text-text-body"
                  >
                    {DROPPED_ACTIONS.map((a) => (
                      <option key={a} value={a} disabled={a === 'loeschen' && !r.deletable}>
                        {t(`import.reimport.dropped.${a}`)}
                      </option>
                    ))}
                  </select>
                  {!r.deletable && <span>{t('import.reimport.deleteBlocked')}</span>}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {chainRows.length > 0 && (
        <>
          <p className="mt-4 text-[11px] uppercase tracking-wide text-text-muted">
            {t('import.reimport.chainsTitle')}
          </p>
          <ul className="mt-1">
            {chainRows.map((r) => {
              const ch = chainChoice(choices, r);
              const collision = isCollision(r);
              // Eine Kette mit Konflikt und ohne e2e_id (Glied kollidiert) wird nie angelegt —
              // ein Häkchen hätte keine Wirkung.
              const selectable =
                !collision &&
                (r.category === 'neu' ||
                  r.category === 'geaendert' ||
                  (r.category === 'konflikt' && r.e2e_id !== null));
              const id = r.human_id ?? r.source_ref;
              const skippedMissing = outlook.chainsSkipped.get(r.key);
              const heldTitles = [...outlook.heldBack]
                .filter(([, ck]) => ck === r.key)
                .map(([k]) => k);
              const missing = outlook.missingMembers.get(r.key);
              const membersDiff = r.diffs.find(
                (d) => d.field === 'members' && d.conflict_reason !== 'collision',
              );
              return (
                <li
                  key={r.key}
                  data-testid={`reimport-chain-${r.key}`}
                  className="border-t border-[var(--border)] py-2 text-xs"
                >
                  <div className="flex items-center gap-2">
                    {selectable && (
                      <input
                        type="checkbox"
                        aria-label={t('import.reimport.select', { id: id ?? r.name })}
                        checked={ch.selected}
                        onChange={(e) => setChain(r, { selected: e.target.checked })}
                      />
                    )}
                    {id && <IdLabel id={id} secondary={r.human_id ? r.source_ref : null} />}
                    <span className="min-w-0 flex-1 truncate text-text-body" title={r.name}>
                      {r.name}
                    </span>
                    <StateChip
                      label={t(`import.reimport.category.${r.category}`)}
                      tone={TONE[r.category]}
                    />
                    {r.category === 'entfaellt' && (
                      <span className="text-text-muted">{t('import.reimport.chainKept')}</span>
                    )}
                    {r.category === 'konflikt' && r.e2e_id === null && !collision && (
                      <span className="text-text-muted">
                        {t('import.reimport.chainNotCreated')}
                      </span>
                    )}
                  </div>
                  {skippedMissing && (
                    <p className="mt-1 text-status-warn">
                      {t('import.reimport.chainSkipped', { members: titles(skippedMissing) })}
                    </p>
                  )}
                  {heldTitles.length > 0 && (
                    <p className="mt-1 text-status-warn">
                      {t('import.reimport.chainHeldBack', { titles: titles(heldTitles) })}
                    </p>
                  )}
                  {missing && (
                    <p className="mt-1 text-status-warn">
                      {t('import.reimport.chainMissing', { members: titles(missing) })}
                    </p>
                  )}
                  {membersDiff && r.e2e_id !== null && (
                    <p className="mt-1 text-text-muted">{t('import.reimport.membersHint')}</p>
                  )}
                  {collision && (
                    <CollisionList items={r.collision.map((it) => ({ ...it, label: it.name }))} />
                  )}
                  {r.diffs.length > 0 && (
                    <div className="mt-1">
                      {diffToggle(
                        `chain:${r.key}`,
                        r.category,
                        r.diffs.map((d) => d.field),
                      )}
                      {isOpen(`chain:${r.key}`, r.category) && (
                        <DiffTable
                          diffs={r.diffs}
                          takeFile={ch.take_file}
                          onToggle={(field, take) =>
                            setChain(r, { take_file: toggleField(ch.take_file, field, take) })
                          }
                        />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
